import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { Router } from '@angular/router';
import { isStringEmpty, stringEmptyDefault } from '@common/helpers';
import { LANGUAGE_EN, LANGUAGE_JP } from '@common/helpers/constants/my-booking/my_booking_service.contant';
import { FEATURE_TO_ROUTE_MAPPING } from '@common/helpers/constants/servicing.contant';
import { linkToPagePostGet } from '@common/helpers/utilities/path-link.helper';
import { LHParams } from '@common/interfaces/mybooking/mybooking-service.interface';
import { SeatReservationStatus } from '@common/interfaces/servicing-seatmap/seat-reservation-status';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingServiceService } from '@common/services/my-booking/my-booking-service/my-booking-service.service';
import { MasterStoreKey } from '@conf/asw-master.config';
import { SupportComponent } from '@lib/components/support-class';
import { DialogClickType, MOffice } from '@lib/interfaces';
import { StaticMsgPipe } from '@lib/pipes';
import { AswMasterService, CommonLibService } from '@lib/services';
import { Subscription, firstValueFrom } from 'rxjs';
import {
  GetOrderResponseData,
  GetOrderResponseDataAir,
  Traveler,
  Type1,
  Type1Arrival,
  Type1Departure,
} from 'src/sdk-servicing';

@Component({
  selector: 'asw-seat-reservation',
  templateUrl: './seat-reservation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeatReservationComponent extends SupportComponent {
  private _subscriptions: Subscription = new Subscription();
  public isScrollSeatReservation: boolean = false;

  pnr!: GetOrderResponseData;

  aswLangCode: string = '';

  seatFlightInforList!: Array<Type1>;

  seatPassengerInforList!: Array<Traveler>;
  seatReservationStatus!: SeatReservationStatus;

  langPosCountryLHOffice: string = '';

  constructor(
    private _common: CommonLibService,
    private _http: HttpClient,
    private readonly _router: Router,
    public change: ChangeDetectorRef,
    private _aswMasterService: AswMasterService,
    private _myBookingServiceService: MyBookingServiceService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _staticMsg: StaticMsgPipe
  ) {
    super(_common);
  }

  init(): void {
    this._subscriptions.add(
      this._myBookingServiceService.mybookingServiceState$.subscribe((state) => {
        this.pnr = state.pnr ?? ({} as GetOrderResponseData);
        // イベントID(1)で取得した事前座席指定セグメント情報リストの繰り返し(それぞれ要素が当該セグメント情報)
        this.seatReservationStatus = state.seatReservationStatus ?? {};
        this.seatFlightInforList = state.seatFlightInforList ?? [];
        this.seatPassengerInforList = state.seatPassengerInforList ?? [];
        this.change.markForCheck();
      })
    );
    this.aswLangCode = this._common.aswContextStoreService.aswContextData?.lang ?? '';
  }

  // ヘルプアイコン
  get getTooltipSeatReservation(): string {
    // tooltip.seatAttribute.int1(PNR.data.air.tripType= "international")
    if (this.pnr?.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.International) {
      return this._staticMsg.transform('tooltip.seatAttribute.int1');
    }
    // tooltip.seatAttribute.dom1(data.air.tripType="domestic")
    return this._staticMsg.transform('tooltip.seatAttribute.dom1');
  }

  /** [3.2] カウチルールのコンテンツ */
  get isContainedCouchFlight(): boolean {
    return Boolean(this.pnr?.seats?.isContainedCouchFlight);
  }

  /** [3.3] 有料席のコンテンツ */
  get isChargeableSeatFlight(): boolean {
    return Boolean(this.pnr?.seats?.isChargeableSeatFlight);
  }

  /** [3.4] LHサイトでの座席指定案内 */
  get isContainedSeatRequestableSegmentInLHSite(): boolean {
    return Boolean(
      this.pnr?.orderEligibilities?.seatmap?.isEligibilityToForward &&
        this.pnr?.air?.isContainedSeatRequestableSegmentInLHSite
    );
  }

  /** Check if table slider is scrolling or not with Seat Reservation */
  scrollSeatReservation(value: boolean) {
    this.isScrollSeatReservation = value;
  }

  /** [3.6] 出発地 */
  getlocationNameDeparture(departure?: Type1Departure): string {
    const locationName = this._myBookingServiceService.onHandleCommonNo4(departure?.locationCode);
    return isStringEmpty(locationName) !== true ? locationName : departure?.locationName ?? '';
  }

  /** [3.7] 到着地 */
  getlocationNameArrival(arrival?: Type1Arrival): string {
    const locationName = this._myBookingServiceService.onHandleCommonNo4(arrival?.locationCode);
    return isStringEmpty(locationName) !== true ? locationName : arrival?.locationName ?? '';
  }

  /** [3.9] 搭乗者姓名 */
  getTravelerName(traveler?: Traveler): string {
    return this._myBookingServiceService.getTravelerName(traveler?.names);
  }

  /** [3.10] 同伴搭乗者姓名 */
  getAccompanyingTraveler(traveler?: Traveler): string {
    /**
     * PNR情報取得API応答.data.travelersを繰り返し、
     * 当該搭乗者情報.accompanyingTravelerId = 当該travelers.idとなるtravelers.name[0]について、title
     * + ” ”(半角スペース)
     * + firstName
     * + ” ”(半角スペース)
     * + lastNameで連結した値
     */
    return this._myBookingServiceService.getAccompanyingTraveler(traveler);
  }

  /** [3.10] 旅行者に同行しています */
  isAccompanyingTraveler(traveler?: Traveler): boolean {
    /**
     * 当該搭乗者情報.accompanyingTravelerId ≠ “”(空欄)(同伴搭乗者がいる)場合、以下内容を併せて表示する。
     *
     */
    return this._myBookingServiceService.isAccompanyingTraveler(traveler);
  }

  /** (10)「座席変更」ボタン押下処理 */
  clickChange(flight: Type1): void {
    /** 1. 当該セグメントのシートマップ(S03-P010)に遷移する。
     * 以下の値をパラメータとして指定し、シートマップ(S03-P010)へ遷移する
     */
    this._servicingCommonStoreService.updateServicingCommon({ displayTargetSegmentId: flight?.id });
    this._router.navigate([FEATURE_TO_ROUTE_MAPPING['S03-P010']]);
  }

  /** (11)「LHサイトでの座席指定案内」リンク押下処理 */
  async clickLHLink(): Promise<void> {
    /** 1. 以下条件のいずれかを満たす場合、遷移先が英語になる旨 */
    const offices_db = await firstValueFrom(
      this._common.aswMasterService.getAswMasterByKey$(MasterStoreKey.OFFICE_ALL)
    );
    const langOffice = offices_db?.find?.(
      (office: MOffice) => office.office_code === this._common.aswContextStoreService.aswContextData.pointOfSaleId
    );
    this.langPosCountryLHOffice = langOffice?.['lh_third_language_code'] ?? '';
    /**1.1. 以下条件のすべてを満たす場合、MSG0642 「遷移先が英語になる旨」を表示する。*/
    /**1.2. 上記以外の場合、MSG0641 「遷移先が他社のページである旨」を表示する。 */
    const message = this.isTransitionDestinationEnglish() ? 'MSG0642' : 'MSG0641';
    const result = await this._myBookingServiceService.openDialogPromise(message);
    /**1.3 確認ダイアログより「閉じる」ボタン押下が返却された場合、イベント処理No11を終了する。 */
    if (result === DialogClickType.CLOSE) {
      return;
      /**1.4 確認ダイアログより「確認」ボタン押下が返却された場合、「当画面共通処理No3. 全サービスの編集情報破棄処理」を実施し、イベント処理No11を終了する。 */
    } else if (result === DialogClickType.CONFIRM) {
      /**2. LHサイト側へ受け渡すパラメータの生成を行う。 */
      // 2.1.5.	「3.1.6 Target URL」
      const urlLH = this._aswMasterService.getMPropertyByKey('seatMap', 'deeplink.lufthansa.url');
      // 2.1.2.	「3.1.1 Authorization Parameters」
      const partnerID = this._aswMasterService.getMPropertyByKey('seatMap', 'deeplink.lufthansa.partnerId');
      // 2.2.	資料中のLanguageとCountryについては、以下の内容とする。
      let lang = '';
      if (this._common.aswContextStoreService.aswContextData?.lang === LANGUAGE_JP) {
        lang = LANGUAGE_JP;
      } else if (this._common.aswContextStoreService.aswContextData?.lang === LANGUAGE_EN) {
        lang = LANGUAGE_EN;
      } else if (
        this.langPosCountryLHOffice !== '' &&
        this.langPosCountryLHOffice === this._common.aswContextStoreService.aswContextData?.lang
      ) {
        lang = this.langPosCountryLHOffice;
      }
      const posLHSite =
        this._common.aswContextStoreService.aswContextData?.posCountryCode === langOffice?.['pos_lh_site']
          ? langOffice?.['pos_lh_site']
          : '';

      const paramsLufthasan: LHParams = {
        partnerId: stringEmptyDefault(partnerID),
        Language: lang,
        Country: posLHSite,
        ENC: stringEmptyDefault(this.pnr?.air?.encryptDataForLHSite),
      };
      // 2.3.	以下の内容をパラメータとして指定し、Target URLを利用し、POSTで送信し、LHサイト(外部サイト)を別タブで表示する。
      linkToPagePostGet(urlLH, paramsLufthasan, '_blank', 'get');
    }
  }

  isTransitionDestinationEnglish(): boolean {
    /**1.1.1.   ユーザ共通.言語情報 != "ja"(日本語)*/
    /**1.1.2.   ユーザ共通.言語情報 != "en"(英語) */
    /**1.1.3.   ASWDB (マスタ).オフィス.LH第3言語コード= " "(空欄) */
    return this.aswLangCode !== LANGUAGE_JP && this.aswLangCode !== LANGUAGE_EN && this.langPosCountryLHOffice === '';
  }

  buf2hex(bufferOrUint8Array: Uint8Array) {
    return [...(bufferOrUint8Array instanceof ArrayBuffer ? new Uint8Array(bufferOrUint8Array) : bufferOrUint8Array)]
      .map((x) => x.toString(16).padStart(2, '0'))
      .join('');
  }

  get langCodeLH(): string {
    let langCodeLH = LANGUAGE_EN;
    // 1. ユーザ共通.言語情報="ja"(日本語)の場合、"ja"(日本語)
    // 2. ユーザ共通.言語情報="en"(英語)の場合、"en"(英語)
    if (this.aswLangCode === LANGUAGE_EN || this.aswLangCode === LANGUAGE_JP) {
      return this.aswLangCode;
    }
    // 3. ルフトハンザ第3言語コード≠""(空欄)かつ、ルフトハンザ第3言語コード=ユーザ共通.言語情報の場合、ルフトハンザ第3言語コード
    if (this.langPosCountryLHOffice && this.langPosCountryLHOffice === this.aswLangCode) {
      return this.langPosCountryLHOffice;
    }
    // 4. 上記以外の場合、"en"(英語)
    return langCodeLH;
  }

  /** [3.17] 座席変更 */
  isSeatChange(flight?: Type1): boolean {
    return this.pnr?.orderEligibilities?.seatmap?.[String(flight?.id)]?.isEligible;
  }
  reload(): void {}

  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }
}
